import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  SimpleGrid,
  Spacer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/Auth";
import { usePremium } from "../../../contexts/Premium";
import { uniqBy } from "../../../core/utils";
import {
  getAllComics,
  getPreviousAllComics,
  getReading,
} from "../../../services/readingOrdersApi";
import Spinner from "../../../shared/components/Spinner";
import {
  getReadingOrder,
  setOrderPosition,
  setPreviousComics,
} from "../actions";
import JumpToModal from "../components/JumpToModal";
import OrderCompleted from "../components/OrderCompleted";
import ReadingOrderActions from "../components/ReadingOrderActions";
import ReadingOrderHeader from "../components/ReadingOrderHeader";
import ReadingOrderList from "../components/ReadingOrderList";
import ReadingStatusFilter from "../components/ReadingStatusFilter";
import UpNext from "../components/UpNext";
import SuggestCorrection from "../../../shared/components/SuggestCorrection";
import useUpgrade from "../../../shared/hooks/useUpgrade";
import PreviewProgress from "../components/PreviewProgress";
import { isBookClubReading, isReadingTrackingReadOnly } from "../readingAccess";
import MigrationWarningModal from "../components/MigrationWarningModal";
import GoalCreateModal from "../../profile/components/goals/GoalCreateModal";

const ReadingOrder = () => {
  const { authData } = useAuth();
  const { isPremium } = usePremium();
  const { readingId } = useParams();
  const dispatch = useDispatch();
  const upgrade = useUpgrade();
  const upNextRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const goalDisclosure = useDisclosure();
  const [selectedFilter, setSelectedFilter] = useState("all");
  const readingOrder = useSelector(
    (state) => state.orders.readings[readingId]?.comics
  );
  const orderPosition = useSelector(
    (state) => state.orders.readings[readingId]?.orderPosition
  );

  const readingQuery = useQuery(
    ["reading", readingId],
    () => getReading(readingId),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const loadPreviousMutation = useMutation(
    ({ readingId, from }) => getPreviousAllComics(readingId, from),
    {
      onSuccess: (responseData, { readingId }) => {
        dispatch(setPreviousComics(readingId, responseData.data));
      },
    }
  );

  const readingData = readingQuery?.data?.data;
  const paywall = readingData?.paywall;
  const isReadOnly = isReadingTrackingReadOnly(readingData, isPremium);
  // Mirrors the mobile ReadingExperienceScreen: book club readings never
  // show the preview footer.
  const showPreviewBar =
    !isReadOnly &&
    !isPremium &&
    !isBookClubReading(readingData) &&
    !!paywall?.previewEnabled &&
    paywall?.previewLimit > 0;
  // Both bottom bars are fixed, so the page needs room to scroll clear of them.
  const bottomBarOffset = isReadOnly || showPreviewBar ? "72px" : undefined;

  const onPressUpNext = useCallback(() => {
    upNextRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const onClickPrevious = useCallback(() => {
    const from = readingOrder[0]?.order;
    loadPreviousMutation.mutate({ readingId: readingId, from });
  }, [loadPreviousMutation, readingOrder, readingId]);

  // Fetch data
  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery(
      ["readingOrder", readingId],
      ({ pageParam = 1 }) => getAllComics(readingId, pageParam),
      {
        keepPreviousData: true,
        staleTime: 3 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: !!readingData,
        getNextPageParam: (pageParam, allPages) => {
          if (pageParam?.meta.currentPage === pageParam?.meta.lastPage) {
            return undefined;
          }
          return pageParam?.meta?.currentPage + 1;
        },
        onSuccess: (responseData) => {
          let comics = [];
          responseData.pages.forEach((page) => {
            comics = [...comics, ...page.data];
          });
          dispatch(getReadingOrder(readingId, comics));
          dispatch(setOrderPosition(readingId, readingData.orderPosition));
        },
      }
    );

  const upNext = useSelector((state) =>
    state.orders.readings[readingId]?.comics?.find((item) =>
      state.orders.readings[readingId].orderPosition
        ? item.order >= state.orders.readings[readingId].orderPosition &&
          !item.read &&
          !item.readLater &&
          !item.skipped
        : !item.read && !item.readLater && !item.skipped
    )
  );

  const filterComics = useCallback(() => {
    if (selectedFilter === "all") {
      return uniqBy(readingOrder);
    }

    return uniqBy(
      readingOrder?.filter((item) => {
        if (selectedFilter === "unread") {
          return !item.skipped && !item.read && !item.readLater;
        }
        if (selectedFilter === "skipped") {
          return item.skipped;
        }
        if (selectedFilter === "readLater") {
          return item.readLater;
        }
        if (selectedFilter === "read") {
          return item.read;
        }
        return item;
      })
    );
  }, [readingOrder, selectedFilter]);

  if (readingQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <Container maxW="container.lg" pb={bottomBarOffset}>
      <SimpleGrid mt="6" columns={{ sm: 1, md: 1, lg: 2 }} spacing={2}>
        <Box>
          <Box bg="gray.700" borderRadius="lg">
            {/* Header */}
            <ReadingOrderHeader
              readingOrder={readingData?.readingOrder}
              totalComics={readingData?.totalComics}
              progress={readingData?.progress || 0}
            />

            {!isReadOnly &&
              (readingData?.progress !== 100 ? (
                <UpNext
                  readingId={readingId}
                  upNext={upNext}
                  totalComics={readingData?.totalComics}
                  isLoading={isLoading}
                  onPress={onPressUpNext}
                  disableTracking={false}
                />
              ) : (
                <OrderCompleted />
              ))}
          </Box>

          {/* Suggest correction */}
          {authData.id !== readingData?.readingOrder?.author?.id && (
            <SuggestCorrection readingOrderId={readingData?.readingOrder?.id} />
          )}
        </Box>

        {/* Reading order header */}
        <Box bg="gray.700" borderRadius="lg">
          <Box
            py="3"
            px="3"
            borderBottomColor="gray.900"
            borderBottomWidth="1px"
          >
            <Flex justifyContent="center">
              <Text fontSize="sm" color="gray.400" mt="2" ml="1">
                READING ORDER
              </Text>
              <Spacer />
              <HStack>
                <ReadingStatusFilter
                  selectedFilter={selectedFilter}
                  setSelectedFilter={setSelectedFilter}
                />
                <ReadingOrderActions
                  readingId={readingId}
                  readingOrder={readingOrder}
                  orderPosition={orderPosition}
                  openModal={onOpen}
                  isReadOnly={isReadOnly}
                  onSetGoal={
                    readingData?.readingOrder?.id &&
                    !isBookClubReading(readingData)
                      ? goalDisclosure.onOpen
                      : undefined
                  }
                />
              </HStack>
            </Flex>
          </Box>

          {Array.isArray(readingOrder) && readingOrder[0]?.order !== 1 && (
            <Box>
              <Button
                onClick={() => onClickPrevious()}
                isLoading={loadPreviousMutation.isLoading}
                disabled={loadPreviousMutation.isLoading}
                loadingText="Loading previous comics"
                w="100%"
                size="sm"
                variant="outline"
              >
                See previous comics
              </Button>
            </Box>
          )}

          {/* Reading order list */}
          <ReadingOrderList
            readingId={readingId}
            upNext={isReadOnly ? null : upNext}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            readingOrderComics={filterComics()}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            upNextRef={upNextRef}
            disableTracking={isReadOnly}
          />
        </Box>
      </SimpleGrid>

      {isReadOnly && (
        <Box
          as="button"
          type="button"
          aria-label="Tracking is paused. Upgrade to continue"
          position="fixed"
          left="0"
          right="0"
          bottom="0"
          zIndex={10}
          h="52px"
          minH="52px"
          px="4"
          py="0"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="#171C2D"
          borderTop="1px solid"
          borderColor="#FFAA00"
          color="gray.300"
          cursor="pointer"
          _hover={{ bg: "#222B45" }}
          onClick={upgrade}
        >
          <Text
            as="span"
            fontSize="0.9em"
            lineHeight="1.25em"
            textAlign="center"
          >
            Tracking is paused{". "}
            <Text as="span" color="#FFAA00" fontWeight="bold">
              Upgrade to continue
            </Text>
          </Text>
        </Box>
      )}

      {showPreviewBar && (
        <Box
          position="fixed"
          left="0"
          right="0"
          bottom="0"
          zIndex={10}
          minH="52px"
          px="2"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="#101426"
          borderTop="1px solid"
          borderColor="#FFAA00"
        >
          <PreviewProgress paywall={paywall} isPremium={isPremium} />
        </Box>
      )}

      {/* Modal */}
      <JumpToModal isOpen={isOpen} onClose={onClose} reading={readingData} />
      <GoalCreateModal
        initial={{
          type: "deadline",
          readingOrderId: readingData?.readingOrder?.id,
          source: "order_detail",
        }}
        isOpen={goalDisclosure.isOpen}
        onClose={goalDisclosure.onClose}
      />
      <MigrationWarningModal
        level={paywall?.warningLevel}
        remaining={paywall?.previewRemaining}
        warningKey={`${authData.id}:${readingId}`}
        isPremium={isPremium}
      />
    </Container>
  );
};

export default ReadingOrder;
